// src/components/functions/NodeOutputsPreview.js
//
// What a processor block produced at build time — a Logic block's implement
// or a Predefined block's preview — rendered from its manifest: the data rows
// with their schema, any captured charts, and the files it ships. Both block
// editors show this pane, so a person reads a node's outputs the same way
// whichever kind of block made them.
import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import PreviewTable from './PreviewTable';
import FileCard from '../agent/parts/FileCard';
import BarChartComponent from '../charts/BarChart';
import LineChartComponent from '../charts/LineChart';
import ScatterChartComponent from '../charts/ScatterChart';
import PieChartComponent from '../charts/PieChart';
import BoxAndWhiskerChartComponent from '../charts/BoxAndWhiskerChart';
import SuperChartComponent from '../charts/SuperChart';
import { fetchGcsObject } from '../../services/api_utils';
import { schemaLeafPaths } from '../../utils/manifestSchema';

export const LazyPreviewTable = ({ gcsPath, liveData }) => {
  const [data, setData] = useState(liveData || null);
  const [loading, setLoading] = useState(!liveData);
  // A saved preview that could not be fetched is a load failure, shown as such
  // with a Retry — never a synthetic empty result, which would read as "the
  // Logic returned no rows".
  const [loadError, setLoadError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;

    if (liveData) {
      setData(liveData);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const loadData = async () => {
      setLoadError(null);
      // Removing a live candidate must also discard this component's cached
      // rows, even when the committed preview path did not change.
      setData(null);
      if (!gcsPath) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const fetchedData = await fetchGcsObject(gcsPath);
        if (!cancelled) {
          setData(fetchedData);
        }
      } catch (error) {
        console.error("Failed to load preview data from GCS", error);
        if (!cancelled) {
          setLoadError(error?.message || 'Unknown error');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadData();
    return () => {
      cancelled = true;
    };
  }, [gcsPath, liveData, attempt]);

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError && !data) {
    return (
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
        <Typography color="error">
          The saved preview could not be loaded. Please retry. If this does not work, please contact support.
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(240, 242, 245, 0.6)', wordBreak: 'break-word' }}>
          {loadError}
        </Typography>
        <Button variant="outlined" size="small" onClick={() => setAttempt((n) => n + 1)}>Retry</Button>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return <Typography sx={{ p: 2 }}>No data to display.</Typography>;
  }

  return <PreviewTable data={data} />;
};

export const formatSize = (sizeBytes) => {
  if (sizeBytes === null || sizeBytes === undefined) return 'Size unavailable';
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
};

const renderChart = (chart) => {
  if (!chart) return null;
  if (chart.type === 'bar') return <BarChartComponent chartData={chart} />;
  if (chart.type === 'line') return <LineChartComponent chartData={chart} />;
  if (chart.type === 'scatter') return <ScatterChartComponent chartData={chart} />;
  if (chart.type === 'pie') return <PieChartComponent chartData={chart} />;
  if (chart.type === 'box_and_whisker') return <BoxAndWhiskerChartComponent chartData={chart} />;
  if (chart.type === 'superchart') return <SuperChartComponent chartData={chart} />;
  return <Typography color="error">Unsupported chart type: {chart.type || 'unknown'}</Typography>;
};

const ManifestChart = ({ entry }) => {
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartError, setChartError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const loadChart = async () => {
      setLoading(true);
      setChartError('');
      try {
        const chartSpec = await fetchGcsObject(entry.gcs_path);
        if (!cancelled) setChart(chartSpec);
      } catch (err) {
        console.error('Failed to load manifest chart', err);
        if (!cancelled) setChartError('Failed to load this chart.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (entry.gcs_path) {
      loadChart();
    } else {
      setLoading(false);
      setChartError('This chart has no persisted file.');
    }
    return () => {
      cancelled = true;
    };
  }, [entry.gcs_path]);

  return (
    <Box sx={{ border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 1, p: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{entry.title || entry.key}</Typography>
      {loading && <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box>}
      {chartError && <Typography color="error">{chartError}</Typography>}
      {chart && <Box sx={{ height: 360, minHeight: 320 }}>{renderChart(chart)}</Box>}
    </Box>
  );
};

export const SchemaChips = ({ schema }) => {
  if (!schema) return null;
  const staticPaths = schemaLeafPaths(schema || {});
  const dynamic = schema?.dynamic;
  const dynamicExamples = dynamic?.present_at_build || [];
  const dynamicChildren = schemaLeafPaths({ static: dynamic?.children || [] });
  const dynamicDetails = [
    dynamicExamples.length > 0 ? `examples: ${dynamicExamples.join(', ')}` : null,
    dynamicChildren.length > 0 ? `fields: ${dynamicChildren.join(', ')}` : null,
    dynamic?.note || null,
  ].filter(Boolean);

  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
      {staticPaths.map(path => (
        <Chip key={path} size="small" label={path} variant="outlined" />
      ))}
      {dynamic && (
        <Chip
          size="small"
          label={`dynamic: ${dynamicDetails.join(' · ') || 'input-dependent columns'}`}
          sx={{ backgroundColor: 'rgba(126, 88, 181, 0.25)', color: '#D4B8FA' }}
        />
      )}
    </Stack>
  );
};

// `fileDownloadUrls` serves a file that is not in storage yet — a preview
// awaiting its save — keyed by manifest key; a saved file downloads by its
// gcs_path.
const NodeOutputsPreview = ({ manifest, outputGcsPath, livePreviewData, fileDownloadUrls = {} }) => {
  const dataEntry = manifest.find(entry => entry.kind === 'data');
  const chartEntries = manifest.filter(entry => entry.kind === 'chart');
  const deliveryEntries = manifest.filter(entry => entry.kind === 'file' || entry.kind === 'folder');

  return (
    <>
      {(dataEntry || outputGcsPath || livePreviewData) && (
        <Box sx={{ mb: 2, border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 1, p: 1.5, backgroundColor: 'rgba(126, 88, 181, 0.05)' }}>
          <Typography variant="subtitle2">Data</Typography>
          {dataEntry?.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {dataEntry.description}
            </Typography>
          )}
          <SchemaChips schema={dataEntry?.schema} />
          <LazyPreviewTable gcsPath={outputGcsPath} liveData={livePreviewData} />
        </Box>
      )}

      {chartEntries.map(entry => <ManifestChart key={entry.key} entry={entry} />)}

      {deliveryEntries.length > 0 && (
        <Stack spacing={1.25} sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Downloads</Typography>
          {deliveryEntries.map((entry) => entry.kind === 'file' ? (
            <FileCard
              key={entry.key}
              filename={entry.filename || entry.key}
              description={formatSize(entry.size_bytes)}
              gcsPath={entry.gcs_path}
              downloadUrl={fileDownloadUrls[entry.key]}
            />
          ) : (
            <Box key={entry.key} sx={{ border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 1, p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {entry.key} · {(entry.files_at_build || []).length} file{(entry.files_at_build || []).length === 1 ? '' : 's'}
              </Typography>
              <Stack spacing={1}>
                {(entry.files_at_build || []).map(file => (
                  <FileCard
                    key={`${entry.key}${file.name}`}
                    filename={file.name}
                    description={formatSize(file.size_bytes)}
                    gcsPath={file.gcs_path}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </>
  );
};

export default NodeOutputsPreview;
